import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });
    
    // ส่ง error ไปยัง error logging service (ถ้ามี)
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            bgcolor: 'background.default',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 4 },
              maxWidth: 600,
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              color="error"
              gutterBottom
              sx={{ mb: 2 }}
            >
              เกิดข้อผิดพลาด
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              ขออภัย เกิดข้อผิดพลาดขึ้นในระบบ กรุณาลองใหม่อีกครั้ง
            </Typography>

            {process.env.NODE_ENV === 'development' && (
              <Box sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" color="error" sx={{ mb: 1, fontFamily: 'monospace' }}>
                  {this.state.error?.toString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Box>
            )}

            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={this.handleRefresh}
              sx={{ minWidth: 200 }}
            >
              โหลดหน้าใหม่
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 